import React, { useEffect, useState } from 'react';
import HelloSign from 'hellosign-embedded';
import { applicationService } from 'src/services/application';
import { useApplicationStore } from 'src/store/application';
import { useAIStore } from 'src/store/ai';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';

const Loading = () => {
  return (
    <div className="flex items-center justify-center h-64">
      <AnimatedDots />
    </div>
  );
}

export const ContractStep = ({ handleNextCompatible, handlePrev }: { handleNextCompatible: () => void, handlePrev: () => void }) => {
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { currentApplicationId } = useApplicationStore();
  const { setChangeAfterEstimation } = useAIStore();

  useEffect(() => {
    if (currentApplicationId) {
      applicationService.getSignUrl(currentApplicationId).then((res) => {
        setSignUrl(res.signUrl);
        setIsLoading(false);
      }).catch((err) => {
        console.log('Error getting sign url', err);
        setError('Unexpected error occurred. Please try later.');
        setIsLoading(false);
      });
    }
  }, [currentApplicationId]);

  useEffect(() => {
    if (!signUrl || !currentApplicationId) {
      return;
    }
    let groupHost = new URL(window.location.href).host;
    let skipDomainVerification = groupHost.includes('localhost')

    try {
      const client = new HelloSign({
        clientId: import.meta.env.VITE_APP_DROPBOX_SIGN_CLIENT_ID,
        skipDomainVerification
      });

      const openTimeout = setTimeout(() => {
        setError('Unable to open signature form. Please try again.');
      }, 10000);

      client.open(signUrl);

      client.on('sign', (data: any) => {
        console.log('Document signed!', data);
        clearTimeout(openTimeout);
        applicationService.postSignUrl(currentApplicationId).then((res) => {
          console.log('Document signed!', res);
          // Reset change after estimation flag after successful contract signing
          setChangeAfterEstimation(false);
          handleNextCompatible();
        }).catch((err) => {
          console.log('Error confirming signature', err);
          setError('Error confirming signature. Please try again.');
        });
      });

      client.on('close', () => {
        console.log('Signature form closed');
        clearTimeout(openTimeout);
        handlePrev();
      });

      client.on('error', (err: any) => {
        console.log('HelloSign error', err);
        clearTimeout(openTimeout);
        setError('Unable to open signature form. Please try again.');
      });

    } catch (err) {
      console.log('Error initializing HelloSign', err);
      setError('Unable to initialize signature form. Please try again.');
    }
  }, [signUrl, currentApplicationId]);

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    setSignUrl(null);
    // Перезагружаем signUrl
    if (currentApplicationId) {
      applicationService.getSignUrl(currentApplicationId).then((res) => {
        setSignUrl(res.signUrl);
        setIsLoading(false);
      }).catch((err) => {
        console.log('Error getting sign url on retry', err);
        setError('Unexpected error occurred. Please try later.');
        setIsLoading(false);
      });
    }
  };

  const handleGoBack = () => {
    setError(null);
    handlePrev();
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md mx-auto p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Signature Error</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={handleGoBack}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!signUrl) {
    return <Loading />;
  }

  return <div id="hellosign-container"></div>;
};
